import { Container } from 'pixi.js';
import { Pool } from '@/core/pool';
import { getTexture } from '@/assets/resolver';
import { MANIFEST } from '@/assets/manifest';
import type { Arena } from '@/arena/arena';
import { FX } from '@/config/fx';
import { Bullet, type AoeEvent, type BulletSpawn, type HitEvent } from './bullet-types';
import { applyMotion, splitAngle, stepMotion } from './bullet-motion';
import { lookFrame, lookFrames, lookScale } from './bullet-look';

export type { AoeEvent, BulletSpawn, HitEvent } from './bullet-types';

const VIS = FX.bullets;

/**
 * Pooled bullet system — player and enemy bullets in one place.
 * No allocations during a fight; spawn takes from the pool, death returns to it.
 * Each side draws in its own layer (docs/12 E3): the game stacks
 * `enemyView` above everything so incoming fire is never hidden.
 */
export class BulletSystem {
  readonly friendlyView = new Container();
  readonly enemyView = new Container();
  private readonly friendlyPool: Pool<Bullet>;
  private readonly enemyPool: Pool<Bullet>;
  private readonly live: Bullet[] = [];
  private readonly aoeEvents: AoeEvent[] = [];
  private readonly hitEvents: HitEvent[] = [];
  /** Homing target for friendly bullets (boss position); null disables steering */
  private homingX = 0;
  private homingY = 0;
  private hasHomingTarget = false;
  /** Stat counter: friendly bullets fired this run */
  friendlyFired = 0;
  /** Animation clock for enemy pulse / fuse frames (docs/04) */
  private clock = 0;

  constructor() {
    this.friendlyPool = BulletSystem.makePool(this.friendlyView, VIS.friendlyPrealloc);
    this.enemyPool = BulletSystem.makePool(this.enemyView, VIS.enemyPrealloc);
  }

  private static makePool(layer: Container, prealloc: number): Pool<Bullet> {
    return new Pool(() => {
      const b = new Bullet();
      layer.addChild(b.sprite);
      return b;
    }, prealloc);
  }

  setHomingTarget(x: number, y: number): void {
    this.homingX = x;
    this.homingY = y;
    this.hasHomingTarget = true;
  }

  clearHomingTarget(): void {
    this.hasHomingTarget = false;
  }

  spawn(opts: BulletSpawn): void {
    const b = this.place(
      opts.friendly, opts.x, opts.y, opts.dirX, opts.dirY, opts.speed, opts.life,
      opts.radius, opts.damage, opts.texKey, opts.rotates,
    );
    b.behavior = opts.behavior ?? 'straight';
    b.drag = opts.drag ?? 0;
    b.turnRate = opts.turnRate ?? 0;
    b.aoeRadius = opts.aoeRadius ?? 0;
    b.aoeDamage = opts.aoeDamage ?? 0;
    b.crit = opts.crit ?? false;
    b.moving = applyMotion(b, opts.motion, opts.curveSign ?? 1);
    if (b.fused && b.split) b.fuse = b.split.after;
    if (opts.friendly) this.friendlyFired++;
  }

  /** Pool → live with the shared fields set (split children use it directly). */
  private place(
    friendly: boolean, x: number, y: number, dirX: number, dirY: number, speed: number,
    life: number, radius: number, damage: number, texKey: string, rotates: boolean,
  ): Bullet {
    const b = (friendly ? this.friendlyPool : this.enemyPool).acquire();
    b.active = true;
    b.x = b.prevX = x;
    b.y = b.prevY = y;
    b.vx = dirX * speed;
    b.vy = dirY * speed;
    b.hx = dirX;
    b.hy = dirY;
    b.life = life;
    b.radius = radius;
    b.damage = damage;
    b.friendly = friendly;
    b.rotates = rotates;
    b.texKey = texKey;
    b.behavior = 'straight';
    b.aoeRadius = 0;
    b.crit = false;
    b.moving = false;
    b.slow = 1;

    const def = MANIFEST.get(texKey);
    b.sprite.texture = getTexture(texKey);
    // Enemy danger look: pulse frames, or a fuse countdown (docs/04)
    b.frames = friendly ? null : lookFrames(texKey);
    b.fused = b.frames !== null && def?.look === 'fuse';
    b.fuse = 0;
    b.phase = this.live.length & 3;
    if (def) b.sprite.anchor.set(def.anchorX, def.anchorY);
    b.sprite.rotation = rotates ? Math.atan2(dirY, dirX) : 0;
    // Friendly fire glows additively but dimmed; enemy shots stay solid/readable
    b.sprite.blendMode = friendly ? 'add' : 'normal';
    b.sprite.alpha = friendly ? VIS.friendlyAlpha : 1;

    // Enemy shots match their hitbox so they never look like they touch
    // without hitting. Friendly shots draw a bit smaller and capped, so
    // plasma orbs and the supershot fan never out-size enemy bullets (E3).
    const tex = b.sprite.texture;
    const aspect = tex.width / Math.max(1, tex.height);
    const visR = friendly
      ? Math.min(radius * VIS.friendlyVisualScale, VIS.friendlyMaxVisualRadius)
      : radius;
    const targetH = visR * 2 * VIS.hitboxVisualRatio * lookScale(b.fused);
    b.sprite.height = targetH;
    b.sprite.width = targetH * aspect;

    b.sprite.visible = true;
    this.live.push(b);
    return b;
  }

  /** Split-on-timer (docs/02 B3): children straight from the pool. */
  private burst(b: Bullet): void {
    const s = b.split!;
    const heading = Math.atan2(b.hy, b.hx);
    for (let k = 0; k < s.count; k++) {
      const a = splitAngle(heading, s, k);
      this.place(
        b.friendly, b.x, b.y, Math.cos(a), Math.sin(a), s.speed, s.life ?? b.life,
        s.radius ?? b.radius, s.damage ?? b.damage, s.bullet ?? b.texKey, b.rotates,
      );
    }
  }

  update(dt: number, arena: Arena): void {
    this.clock += dt;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const b = this.live[i]!;
      b.prevX = b.x;
      b.prevY = b.y;

      if (b.behavior === 'decel' && b.drag > 0) {
        const d = Math.exp(-b.drag * dt);
        b.vx *= d;
        b.vy *= d;
      } else if (b.behavior === 'homing' && b.friendly && this.hasHomingTarget) {
        const speed = Math.hypot(b.vx, b.vy);
        const current = Math.atan2(b.vy, b.vx);
        const target = Math.atan2(this.homingY - b.y, this.homingX - b.x);
        let diff = target - current;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        const maxTurn = b.turnRate * dt;
        const a = current + Math.max(-maxTurn, Math.min(maxTurn, diff));
        b.vx = Math.cos(a) * speed;
        b.vy = Math.sin(a) * speed;
      }

      // Stasis fields (Chronos) scale this one step; the scale resets after
      const step = dt * b.slow;
      b.slow = 1;
      if (b.moving && stepMotion(b, step)) {
        this.burst(b);
        this.kill(i);
        continue;
      }
      b.x += b.vx * step;
      b.y += b.vy * step;
      b.life -= step;
      if (b.rotates) b.sprite.rotation = Math.atan2(b.vy, b.vx);

      if (
        b.life <= 0 ||
        !arena.contains(b.x, b.y, b.radius) ||
        arena.hitsObstacle(b.x, b.y, b.radius)
      ) {
        this.kill(i);
      }
    }
  }

  /**
   * Kill bullets overlapping the circle and report their damage. `onHit`
   * may return the damage the target ACTUALLY took (directional or partial
   * defences): the number then shows exactly that, and 0 shows none.
   * @param report false = armor/decoy "clink": no damage-number event
   */
  collideCircle(
    friendly: boolean,
    x: number,
    y: number,
    radius: number,
    onHit: (damage: number, bx: number, by: number) => number | void,
    report = true,
  ): number {
    let hits = 0;
    for (let i = this.live.length - 1; i >= 0; i--) {
      // onHit callbacks may mutate the list (clearAll etc.) — re-clamp.
      if (i >= this.live.length) {
        i = this.live.length;
        continue;
      }
      const b = this.live[i]!;
      if (b.friendly !== friendly || !b.active) continue;
      if (Math.hypot(b.x - x, b.y - y) < b.radius + radius) {
        const { x: bx, y: by, damage, crit } = b;
        this.kill(i);
        const taken = onHit(damage, bx, by);
        const exact = typeof taken === 'number';
        const shown = exact ? taken : damage;
        if (report && shown > 0) {
          this.hitEvents.push({ x: bx, y: by, tx: x, ty: y, tr: radius, damage: shown, crit, friendly, exact });
        }
        hits++;
      }
    }
    return hits;
  }

  /** Clear all bullets (arena transition, restart, screen-clear bomb). */
  clearAll(friendlyOnly?: boolean): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const b = this.live[i]!;
      if (friendlyOnly !== undefined && b.friendly !== friendlyOnly) continue;
      b.aoeRadius = 0; // suppress explosions on bulk clears
      this.kill(i);
    }
    this.aoeEvents.length = 0;
    this.hitEvents.length = 0;
  }

  /** Consume queued impact events (floating damage numbers). */
  drainHitEvents(): HitEvent[] {
    const out = this.hitEvents.slice();
    this.hitEvents.length = 0;
    return out;
  }

  /** Consume queued area-damage events (mortar/rocket explosions). */
  drainAoeEvents(): AoeEvent[] {
    const out = this.aoeEvents.slice();
    this.aoeEvents.length = 0;
    return out;
  }

  private kill(index: number): void {
    const b = this.live[index];
    if (!b || !b.active) return; // guard against nested mutation during callbacks
    if (b.aoeRadius > 0) {
      this.aoeEvents.push({
        x: b.x, y: b.y, radius: b.aoeRadius, damage: b.aoeDamage, friendly: b.friendly,
      });
    }
    b.active = false;
    b.sprite.visible = false;
    const last = this.live.length - 1;
    this.live[index] = this.live[last]!;
    this.live.pop();
    (b.friendly ? this.friendlyPool : this.enemyPool).release(b);
  }

  render(alpha: number): void {
    for (const b of this.live) {
      b.sprite.position.set(
        b.prevX + (b.x - b.prevX) * alpha,
        b.prevY + (b.y - b.prevY) * alpha,
      );
      if (b.frames) b.sprite.texture = lookFrame(b, this.clock);
    }
  }

  get count(): number {
    return this.live.length;
  }

  /** Live bullets for field effects (bullet-fields.ts) — read/steer only. */
  get liveList(): ReadonlyArray<Bullet> {
    return this.live;
  }
}
